import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../config/api';

const CreditsContext = createContext();

export const useCredits = () => {
  return useContext(CreditsContext);
};

export const CreditsProvider = ({ children }) => {
  const { currentUser, getIdToken } = useAuth();
  const [credits, setCredits] = useState(0);
  const [totalPurchased, setTotalPurchased] = useState(0);
  const [totalUsed, setTotalUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user credits when user changes
  useEffect(() => {
    fetchUserCredits();
  }, [currentUser]);

  // Function to fetch user's credit balance

  const fetchUserCredits = useCallback(async () => {
    if (!currentUser) {
      setCredits(0);
      setTotalPurchased(0);
      setTotalUsed(0);
      setLoading(false);
      return;
    }
  
    try {
      setLoading(true);
      const token = await getIdToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
  
      const response = await fetch(`${API_BASE_URL}/user/credits`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
  
      if (!response.ok) {
        // If user document doesn't exist yet, create it
        if (response.status === 404) {
          const createResponse = await fetch(`${API_BASE_URL}/auth/create-user-doc`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              initialCredits: 25 // Default initial credits
            })
          });
          
          if (createResponse.ok) {
            // Retry fetching credits after creating the user doc
            return fetchUserCredits();
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      
      setCredits(data.credits || 0);
      setTotalPurchased(data.total_purchased || 0);
      setTotalUsed(data.total_used || 0);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      setError('Failed to load credit information');
    } finally {
      setLoading(false);
    }
  }, [currentUser, getIdToken]);

  // Function to initialize Paystack payment
  const initializePayment = async (amount, packageType, amountUSD = null) => {
    try {
      if (!currentUser) {
        throw new Error('Authentication required');
      }
  
      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/payment/initialize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amount, // Amount in ZAR cents
          amountUSD: amountUSD, // Original USD amount in cents (for reference)
          package: packageType, // '5', '15', '25', or 'flexi'
          callback_url: window.location.origin + '/credits',
          currency: 'ZAR' // Explicitly set currency to ZAR
        })
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment initialization failed');
      }
  
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Payment initialization error:', error);
      throw error;
    }
  };

  // Function to verify payment (after redirect from Paystack)
  const verifyPayment = async (reference) => {
    try {
      if (!currentUser) {
        throw new Error('Authentication required');
      }

      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/payment/verify/${reference}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment verification failed');
      }

      const data = await response.json();
      
      // Refresh credits after successful payment
      if (data.status === 'success') {
        await fetchUserCredits();
      }
      
      return data;
    } catch (error) {
      console.error('Payment verification error:', error);
      throw error;
    }
  };

  const value = {
    credits,
    totalPurchased,
    totalUsed,
    loading,
    error,
    fetchUserCredits,
    initializePayment,
    verifyPayment
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
};